import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Calendar,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputArea,
  InputField,
  Radio,
  Tag,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "dayjs/locale/ko";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./index.scss";
import zoomTimezone from "./zoomTimezone";
import utilsCommon from "../../../shared/utils/common";
import { RecurrenceData, ZoomMeetingRequestDto } from "../../../types/meeting";
import dayjs from "dayjs";
import useMeetingStore from "../../../redux/dispatcher/useMeetingStore";
import UserSearchField from "../../../components/UserSearchField";
import { Member } from "../../../types/workgroup";
import useWorkgroupStore from "../../../redux/dispatcher/useWorkgroupStore";
import { getUserInfoFromLocalStorage } from "../../../shared/utils/auth";
import { LoginDto } from "../../../types/auth";
import SelectParticipantModal from "./SelectParticipantModal";
import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import NameTag from "../../../components/NameTag";
import WorkgroupManagerIcon from "../../../components/WorkgroupManagerIcon";
import i18n from "../../../locales/i18n";
import { t } from "i18next";
/** 요일 관련 정보 / resource: 짧은 요일, fullResoure: 긴 요일 */
const DAY_LIST = [
  {
    resource: t("meeting.recurring.mon"),
    fullResource: t("meeting.recurring.monday"),
    key: "2",
  },
  {
    resource: t("meeting.recurring.tues"),
    fullResource: t("meeting.recurring.tuesday"),
    key: "3",
  },
  {
    resource: t("meeting.recurring.wed"),
    fullResource: t("meeting.recurring.wednesday"),
    key: "4",
  },
  {
    resource: t("meeting.recurring.thus"),
    fullResource: t("meeting.recurring.thursday"),
    key: "5",
  },
  {
    resource: t("meeting.recurring.fri"),
    fullResource: t("meeting.recurring.friday"),
    key: "6",
  },
  {
    resource: t("meeting.recurring.sat"),
    fullResource: t("meeting.recurring.saturday"),
    key: "7",
  },
  {
    resource: t("meeting.recurring.sun"),
    fullResource: t("meeting.recurring.sunday"),
    key: "1",
  },
];
/** 반복 주기 관련 정보 */
const REPEAT_MEETING_TYPE = [
  { label: t("meeting.recurring.repeatByDay"), type: 1 },
  { label: t("meeting.recurring.repeatByWeek"), type: 2 },
  { label: t("meeting.recurring.repeatByMonth"), type: 3 },
];
const MeetingForm = ({
  zoomMeetingId,
  workgroupId,
  onBack,
  handleAfterSubmit,
}: {
  zoomMeetingId?: string; // 해당 id가 있으면 수정으로 간주
  workgroupId: string; // 화상회의가 포함된 워크그룹
  onBack: () => void; // 생성, 수정 페이지는 필수로 돌아가기 페이지가 있다.
  handleAfterSubmit?: () => void; // 생성 후 정보
}) => {
  // 아래가 있어야 timezone 적용됨
  dayjs.locale("ko");
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const user = getUserInfoFromLocalStorage();
  const loginDto: LoginDto = user ? JSON.parse(user) : ({} as LoginDto);

  /** Hook 정리 */
  const toast = useToast();
  const { workgroupState } = useWorkgroupStore();
  const {
    meetingState,
    getZoomDetailWithDiff,
    getMeetingDetail,
    createNewMeeting,
    updateMeeting,
    getmeetingList,
  } = useMeetingStore();

  /** State 정리 */
  const [meetingInfo, changeMeetingInfo] = useState<ZoomMeetingRequestDto>({
    topic: "",
    type: "2",
    start_time: dayjs()
      .set("minute", dayjs().get("minute") <= 30 ? 30 : 0)
      .set("hour", dayjs().get("hour") + (dayjs().get("minute") > 30 ? 1 : 0))
      .format("YYYY-MM-DDTHH:mm:ssZ"),
    duration: "40",
    timezone: "Asia/Seoul",
    password: utilsCommon.getNewRandomMeetingPW(),
    agenda: "",
    settings: {
      host_video: false,
      participant_video: false,
      cn_meeting: false,
      in_meeting: false,
      join_before_host: false,
      mute_upon_entry: false,
      watermark: false,
      use_pmi: false,
      approval_type: 2,
      audio: "both",
      auto_recording: "none",
      enforce_login: false,
      enforce_login_domains: "",
      alternative_hosts: "",
      close_registration: false,
      registrants_confirmation_email: true,
      waiting_room: true,
      request_permission_to_unmute_participants: false,
      registrants_email_notification: true,
      meeting_authentication: false,
    },
    recurrence: undefined,
  }); // 생성 또는 수정할 화상회의 정보. 수정정보가 있으면 해당 정보를, 아니면 초기 정보
  const [isReccurenceData, toggleRecurrenceData] = useState(false); // 되풀이 여부
  const [isParticipantsListExtend, changeParticipantsListExtend] =
    useState(false); // 멤버 리스트 창 길이 확장 여부
  const [openWorkgroupMemberModal, toggleWorkgroupMemberModal] =
    useState(false); // 참석자 선택 모달 열림 여부
  const [recurrenceData, setRecurrenceData] = useState<RecurrenceData>({
    type: 1,
    repeat_interval: 1,
    weekly_days: "",
    temp_weekly_days: "",
    monthly_day: dayjs().get("D"),
    monthly_week: Math.ceil(
      (dayjs().date() + dayjs().startOf("month").day()) / 7
    ),
    monthly_week_day: dayjs().get("days") + 1,
    end_times: 1,
    end_date_time: dayjs().local().format("YYYY-MM-DDTHH:mm:ssZ"),
  }); // 되풀이 정보 여부
  const [buttonClick, setButtonClick] = useState(0); // state용
  const [selectReccurrenceType, changeSelectReccureneceType] = useState({
    repeat_month_type: 0, // 0 : 날짜 , 1: 요일
    end_type: 0, // 0 : 날짜 , 1: 횟수
  }); // 되풀이 내 되풀이 기본 정보
  const [participantList, changeParticipantList] = useState<Member[]>([]); // 참석자 리스트

  /** useEffect 정보 */
  useEffect(() => {
    const isEditOne = !!zoomMeetingId && zoomMeetingId?.split("/")[1] !== "ALL";
    if (zoomMeetingId) {
      isEditOne
        ? getMeetingDetail({
            workgroupId: workgroupId,
            meetingId: zoomMeetingId.split("/")[0],
            occurrenceId:
              zoomMeetingId.split("/")[1] === "NONE"
                ? undefined
                : zoomMeetingId.split("/")[1],
          })
        : getZoomDetailWithDiff({
            workgroupId,
            meetingId: zoomMeetingId.split("/")[0],
            occurrenceId:
              zoomMeetingId.split("/")[1] === "NONE"
                ? undefined
                : zoomMeetingId.split("/")[1],
          });
    }
    if (workgroupId)
      changeParticipantList(
        workgroupState.workgroupDetail?.workgroup.members.filter(
          (user: Member) => user.userId === loginDto.userInfo.userId
        )
      );
  }, [zoomMeetingId, workgroupState?.workgroupDetail?.workgroup.members]);
  useEffect(() => {
    if (!meetingState.meetingDetail || !zoomMeetingId) return;
    changeMeetingInfo({
      ...meetingState.meetingDetail.zoomMeetingDetailDTO,
    });
    changeParticipantList(meetingState.meetingDetail.participantList);
    if (meetingState.meetingDetail.zoomMeetingDetailDTO.recurrence) {
      toggleRecurrenceData(true);
      setRecurrenceData({
        ...{
          type: 1,
          repeat_interval: 1,
          weekly_days: "",
          temp_weekly_days: "",
          monthly_day: dayjs().get("D"),
          monthly_week: Math.ceil(
            (dayjs().date() + dayjs().startOf("month").day()) / 7
          ),
          monthly_week_day: dayjs().get("days") + 1,
          end_times: 1,
          end_date_time: dayjs().local().format("YYYY-MM-DDTHH:mm:ssZ"),
        },
        ...meetingState.meetingDetail.zoomMeetingDetailDTO.recurrence,
      });
      changeSelectReccureneceType({
        repeat_month_type: 0,
        end_type: meetingState.meetingDetail.zoomMeetingDetailDTO.recurrence
          .end_times
          ? 1
          : 0,
      });
    } else if (meetingState.meetingDetail.zoomMeetingDetailDTO.type) {
      toggleRecurrenceData(false);
      setRecurrenceData({
        type: 1,
        repeat_interval: 1,
        weekly_days: "",
        temp_weekly_days: "",
        monthly_day: dayjs().get("D"),
        monthly_week: Math.ceil(
          (dayjs().date() + dayjs().startOf("month").day()) / 7
        ),
        monthly_week_day: dayjs().get("days") + 1,
        end_times: 1,
        end_date_time: dayjs().local().format("YYYY-MM-DDTHH:mm:ssZ"),
      });
    }
  }, [meetingState.meetingDetail]);
  // useEffect(() => {
  //   if (!zoomMeetingId) return;
  //   if (meetingState.meetingDetailWithDiff.recurrence) {
  //     setRecurrenceData({
  //       ...recurrenceData,
  //       ...meetingState.meetingDetailWithDiff.entity.recurrence,
  //     });
  //     toggleRecurrenceData(true);
  //   } else if (meetingState.meetingDetail.zoomMeetingDetailDTO.type) {
  //     toggleRecurrenceData(false);
  //     setRecurrenceData({
  //       type: 1,
  //       repeat_interval: 1,
  //       weekly_days: "",
  //       temp_weekly_days: "",
  //       monthly_day: dayjs().get("D"),
  //       monthly_week: Math.ceil(
  //         (dayjs().date() + dayjs().startOf("month").day()) / 7
  //       ),
  //       monthly_week_day: dayjs().get("days") + 1,
  //       end_times: 1,
  //       end_date_time: dayjs().local().format("YYYY-MM-DDTHH:mm:ssZ"),
  //     });
  //   }
  // }, [meetingState.meetingDetailWithDiff]);
  /** 함수 정보 */
  /** 화상회의 정보 수정*/
  const handleChangeMeetingInfo = (key: string, value: string | number) => {
    changeMeetingInfo({ ...meetingInfo, [key]: value });
    setButtonClick(buttonClick + 1);
  };
  /** 화상회의 되풀이 정보 수정*/
  const handleChangeRecurrenceData = (key: string, value: string | number) => {
    setRecurrenceData({ ...recurrenceData, [key]: value });
    setButtonClick(buttonClick + 1);
  };
  /** UI: 되풀이 반복 기준 관련 */
  const renderRepeatPeriod = (type: number, isEditOne: boolean) => {
    return (
      <Box classes="mg mt-8">
        {type === 1 ? (
          <Box alignItems="center">
            <Typography
              type="subtitle2"
              width={80}
              classes="mg mr-8"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("meeting.recurring.everyPeriod")}
            </Typography>
            <Dropdown
              placeholder=" "
              value={recurrenceData.repeat_interval + ""}
              dropdownMaxHeight={200}
              width={120}
              buttonClick={buttonClick}
              disabled={isEditOne}
            >
              {Array(15)
                .fill(1)
                .map((item, index) => (
                  <DropdownItem
                    label={index + 1}
                    onClick={() =>
                      handleChangeRecurrenceData("repeat_interval", index + 1)
                    }
                  />
                ))}
            </Dropdown>
            <Typography type="body2_rg" classes="mg ml-8">
              {t("meeting.recurring.perDay")}
            </Typography>
          </Box>
        ) : type === 2 ? (
          <Box direction="column">
            <Box alignItems="center">
              <Typography
                type="subtitle2"
                width={80}
                classes="mg mr-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("meeting.recurring.everyPeriod")}
              </Typography>
              <Dropdown
                placeholder=" "
                value={recurrenceData.repeat_interval + ""}
                dropdownMaxHeight={200}
                width={120}
                buttonClick={buttonClick}
                disabled={isEditOne}
              >
                {Array(12)
                  .fill(1)
                  .map((item, index) => (
                    <DropdownItem
                      label={index + 1}
                      onClick={() =>
                        handleChangeRecurrenceData("repeat_interval", index + 1)
                      }
                    />
                  ))}
              </Dropdown>
              <Typography classes="mg ml-8 mr-8 " type="body2_rg">
                {t("meeting.recurring.perWeek")}
              </Typography>
            </Box>
            <Box classes="mg mt-8" height={40} alignItems="center">
              <Typography
                type="subtitle2"
                width={80}
                classes="mg mr-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("meeting.recurring.days")}
              </Typography>
              {DAY_LIST.map((item) => (
                <Checkbox
                  label={item.resource}
                  disable={isEditOne}
                  check={recurrenceData.weekly_days?.includes(item.key)}
                  classDiv="mg mr-16"
                  onClick={() =>
                    setRecurrenceData({
                      ...recurrenceData,
                      weekly_days: recurrenceData.weekly_days?.includes(
                        item.key
                      )
                        ? recurrenceData.weekly_days
                            .split(",")
                            .filter((day) => day && day !== item.key)
                            .sort((a, b) => parseInt(a) - parseInt(b))
                            .join(",")
                        : `${recurrenceData.weekly_days},${item.key}`
                            .split(",")
                            .filter((day) => day)
                            .sort((a, b) => parseInt(a) - parseInt(b))
                            .join(","),
                    })
                  }
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box direction="column">
            <Box alignItems="center">
              <Typography
                type="subtitle2"
                width={80}
                classes="mg mr-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("meeting.recurring.everyPeriod")}
              </Typography>
              <Box alignItems="center">
                <Dropdown
                  placeholder=" "
                  value={recurrenceData.repeat_interval + ""}
                  dropdownMaxHeight={200}
                  width={120}
                  disabled={isEditOne}
                  buttonClick={buttonClick}
                >
                  {Array(3)
                    .fill(1)
                    .map((item, index) => (
                      <DropdownItem
                        label={index + 1}
                        onClick={() =>
                          handleChangeRecurrenceData(
                            "repeat_interval",
                            index + 1
                          )
                        }
                      />
                    ))}
                </Dropdown>
                <Typography type="body2_rg" classes="mg ml-8">
                  {t("meeting.recurring.perMonth")}
                </Typography>
              </Box>
            </Box>
            <Box classes="mg mt-8">
              <Typography
                type="subtitle2"
                width={80}
                classes="mg mr-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("meeting.recurring.dateAndday")}
              </Typography>
              <Box direction="column">
                {Array(2)
                  .fill("")
                  .map((item, idx) => (
                    <Box
                      alignItems="center"
                      height={40}
                      classes={classNames({ "mg mt-8": idx === 1 })}
                    >
                      <Radio
                        check={idx === selectReccurrenceType.repeat_month_type}
                        label={t("meeting.recurring.permonth_" + idx)}
                        onClick={() => {
                          changeSelectReccureneceType({
                            ...selectReccurrenceType,
                            repeat_month_type: idx,
                          });
                          setButtonClick(buttonClick + 1);
                        }}
                        disable={isEditOne}
                      />
                      {idx === 0 ? (
                        <Box alignItems="center">
                          <Dropdown
                            placeholder=" "
                            value={recurrenceData.monthly_day + ""}
                            width={120}
                            dropdownMaxHeight={200}
                            disabled={
                              selectReccurrenceType.repeat_month_type !== 0 ||
                              isEditOne
                            }
                            buttonClick={buttonClick}
                            classes="mg ml-8"
                          >
                            {Array(31)
                              .fill(0)
                              .map((item, index) => (
                                <DropdownItem
                                  label={index + 1}
                                  onClick={() =>
                                    handleChangeRecurrenceData(
                                      "monthly_day",
                                      index + 1
                                    )
                                  }
                                />
                              ))}
                          </Dropdown>
                          <Typography classes="mg ml-8" type="body2_rg">
                            {t(`meeting.recurring.oftheMonth_${idx}`)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box alignItems="center">
                          <Dropdown
                            placeholder=" "
                            value={
                              [
                                t("meeting.recurring.first"),
                                t("meeting.recurring.second"),
                                t("meeting.recurring.third"),
                                t("meeting.recurring.fourth"),
                                t("meeting.recurring.last"),
                              ].filter(
                                (item, idx) =>
                                  idx + 1 === recurrenceData.monthly_week
                              )[0]
                            }
                            disabled={
                              selectReccurrenceType.repeat_month_type === 0 ||
                              isEditOne
                            }
                            width={100}
                            buttonClick={buttonClick}
                            classes="mg ml-8"
                          >
                            {[
                              t("meeting.recurring.first"),
                              t("meeting.recurring.second"),
                              t("meeting.recurring.third"),
                              t("meeting.recurring.fourth"),
                              t("meeting.recurring.last"),
                            ].map((item, index) => (
                              <DropdownItem
                                label={item}
                                onClick={() =>
                                  handleChangeRecurrenceData(
                                    "monthly_week",
                                    index + 1
                                  )
                                }
                              />
                            ))}
                          </Dropdown>
                          <Dropdown
                            placeholder=" "
                            disabled={
                              selectReccurrenceType.repeat_month_type === 0 ||
                              isEditOne
                            }
                            value={
                              DAY_LIST.filter(
                                (item, idx) =>
                                  idx + 1 === recurrenceData.monthly_week_day
                              )[0]?.fullResource
                            }
                            width={100}
                            buttonClick={buttonClick}
                            classes="mg ml-8"
                          >
                            {DAY_LIST.map((item, index) => (
                              <DropdownItem
                                label={item.fullResource}
                                onClick={() =>
                                  handleChangeRecurrenceData(
                                    "monthly_week_day",
                                    index + 1
                                  )
                                }
                              />
                            ))}
                          </Dropdown>
                          <Typography classes="mg ml-8" type="body2_rg">
                            {t("meeting.recurring.oftheMonth_1")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  /** UI: 되풀이 관련해서 큰 틀*/
  const renderRepeatMeeting = () => {
    const isEditOne = !!zoomMeetingId && zoomMeetingId?.split("/")[1] !== "ALL";
    return (
      <Box direction="column" classes="mg mt-12 recurrence">
        <Typography type="subtitle1">{t("meeting.recurring.title")}</Typography>
        <Box classes="mg mt-20">
          <Typography
            type="subtitle2"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            width={220}
            classes="mg mr-28"
          >
            {t("meeting.recurring.recurrence")}
          </Typography>
          <Box
            direction="column"
            alignItems="flex-start"
            classes="recurrence-radios"
          >
            <Dropdown
              placeholder=" "
              width={240}
              buttonClick={buttonClick}
              disabled={isEditOne}
              value={
                REPEAT_MEETING_TYPE.filter(
                  (item) => item.type === recurrenceData.type
                )[0]?.label
              }
            >
              {REPEAT_MEETING_TYPE.map((item, index) => (
                <DropdownItem
                  label={item.label}
                  type="check"
                  checkColor={index + 1 === recurrenceData.type}
                  checked={index + 1 === recurrenceData.type}
                  onClick={() => {
                    setButtonClick(buttonClick + 1);
                    setRecurrenceData({ ...recurrenceData, type: index + 1 });
                  }}
                  disabled={isEditOne}
                />
              ))}
            </Dropdown>
            {renderRepeatPeriod(recurrenceData.type, isEditOne)}
          </Box>
        </Box>
        <Box classes="mg mt-36">
          <Typography
            type="subtitle2"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            width={220}
            classes="mg mr-28"
          >
            {t("meeting.recurring.endBy")}
          </Typography>
          <Box
            direction="column"
            alignItems="flex-start"
            classes="recurrence-radios"
          >
            <Box direction="column" alignItems="flex-start">
              <Radio
                label={t("meeting.recurring.date")}
                check={selectReccurrenceType.end_type === 0}
                onClick={() =>
                  changeSelectReccureneceType({
                    ...selectReccurrenceType,
                    end_type: 0,
                  })
                }
                disable={isEditOne}
              />
              {selectReccurrenceType.end_type === 0 && (
                <Calendar
                  type={"single"}
                  selected={
                    new Date(dayjs(recurrenceData?.end_date_time).valueOf())
                  }
                  disabled={isEditOne}
                  width={200}
                  onSubmit={(end) =>
                    setRecurrenceData({
                      ...recurrenceData,
                      end_date_time: dayjs(
                        end instanceof Date ? end.valueOf() : ""
                      ).format("YYYY-MM-DDTHH:mm:ssZ"),
                    })
                  }
                  minDate={new Date(dayjs().startOf("d").valueOf())}
                  classes="mg ml-28 mt-8"
                />
              )}
            </Box>
            <Box direction="column" alignItems="flex-start" classes="mg mt-20">
              <Radio
                disable={isEditOne}
                label={t("meeting.recurring.count")}
                check={selectReccurrenceType.end_type === 1}
                onClick={() =>
                  changeSelectReccureneceType({
                    ...selectReccurrenceType,
                    end_type: 1,
                  })
                }
              />
              <Box alignItems="center" classes="mg ml-28 mt-8">
                <Typography classes="mg mr-8 " type="body2_rg">
                  {t("meeting.recurring.after")}
                </Typography>
                <Dropdown
                  dropdownMaxHeight={200}
                  btnStyle="line"
                  placeholder=" "
                  width={120}
                  value={recurrenceData.end_times + ""}
                  buttonClick={buttonClick}
                  disabled={selectReccurrenceType.end_type === 0 || isEditOne}
                >
                  {Array(20)
                    .fill(0)
                    .map((item, index) => (
                      <DropdownItem
                        label={index + 1}
                        onClick={() =>
                          handleChangeRecurrenceData("end_times", index + 1)
                        }
                      />
                    ))}
                </Dropdown>
                <Typography classes="mg ml-8 mr-8 " type="body2_rg">
                  {t("meeting.recurring.countEnd")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };
  /** 화상회의 생성 또는 수정 */
  const handleCreateOrUpdateMeeting = async () => {
    const utcStartTime = dayjs.tz(
      dayjs(meetingInfo.start_time).format(
        i18n.language.includes("ar") ? "DD/MM/YYYY HH:mm" : "YYYY-MM-DD HH:mm"
      ),
      meetingInfo.timezone
    );
    const newZoomMeetingDTO = {
      openStatus: "PUBLIC",
      participantList: participantList?.map((item) => item.userId),
      zoomCreateOrUpdateMeetingRequestDTO: {
        ...meetingInfo,
        type: isReccurenceData ? "8" : "2",
        start_time: utcStartTime.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        recurrence: isReccurenceData
          ? {
              type: recurrenceData.type,
              repeat_interval: recurrenceData.repeat_interval,
              weekly_days:
                recurrenceData.type === 2
                  ? recurrenceData.weekly_days
                  : undefined,
              monthly_day:
                recurrenceData.type === 3
                  ? recurrenceData.monthly_day
                  : undefined,
              monthly_week:
                recurrenceData.type === 3
                  ? recurrenceData.monthly_week
                  : undefined,
              monthly_week_day:
                recurrenceData.type === 3
                  ? recurrenceData.monthly_week_day
                  : undefined,
              end_times:
                selectReccurrenceType.end_type !== 0
                  ? recurrenceData.end_times
                  : undefined,
              end_date_time:
                selectReccurrenceType.end_type === 0
                  ? dayjs(recurrenceData.end_date_time).format(
                      "YYYY-MM-DDTHH:mm:ss"
                    ) + "Z"
                  : undefined,
            }
          : null,
      },
    };
    const resp: any = !zoomMeetingId
      ? await createNewMeeting({
          workgroupId: workgroupId,
          CreateOrUpdateZoomMeetingDTO: newZoomMeetingDTO,
        })
      : await updateMeeting({
          workgroupId: workgroupId,
          meetingId: zoomMeetingId.split("/")[0],
          occurenceId:
            zoomMeetingId.split("/")[1] === "NONE"
              ? undefined
              : zoomMeetingId.split("/")[1],
          CreateOrUpdateZoomMeetingDTO: {
            ...newZoomMeetingDTO,
            zoomCreateOrUpdateMeetingRequestDTO: {
              topic:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.topic,
              type: newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.type,
              start_time:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO
                  .start_time,
              duration:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.duration,
              timezone:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.timezone,
              password:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.password,
              agenda:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.agenda,
              recurrence:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO
                  .recurrence,
              settings:
                newZoomMeetingDTO.zoomCreateOrUpdateMeetingRequestDTO.settings,
            },
          },
        });
    if (resp.payload) {
      getmeetingList({
        workgroupId: workgroupId,
        zoomDto: {
          startTime: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          timeDirection: "future",
          resultSize: 20,
          pageNo: 1,
        },
      });

      toast.toastMsg(
        nanoid(),
        zoomMeetingId
          ? t("meeting.alert.successEdit")
          : t("meeting.alert.successCreate"),
        "success"
      );
      onBack();
      handleAfterSubmit && handleAfterSubmit();
    } else {
      toast.toastMsg(
        nanoid(),
        resp.error?.message ??
          (zoomMeetingId
            ? t("meeting.alert.failEdit")
            : t("meeting.alert.failCreate")),
        "error"
      );
    }
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"meetingForm"}>
      {openWorkgroupMemberModal && (
        <SelectParticipantModal
          open={openWorkgroupMemberModal}
          participantList={participantList}
          userList={workgroupState.workgroupDetail?.workgroup?.members}
          manager={workgroupState.workgroupDetail?.workgroup?.managerId}
          onClose={() => toggleWorkgroupMemberModal(false)}
          onSubmit={(newMemberList) => {
            changeParticipantList([
              workgroupState.workgroupDetail?.workgroup.members.filter(
                (user: Member) => user.userId === loginDto.userInfo.userId
              )[0],
              ...workgroupState.workgroupDetail?.workgroup.members.filter(
                (user: Member) =>
                  newMemberList.includes(user.userId) &&
                  user.userId !== loginDto.userInfo.userId
              ),
            ]);
          }}
        />
      )}
      <Box direction="column" classes="meetingForm-body">
        <Box height={80} minHeight={80} alignItems="center">
          <Button
            btnStyle="icon"
            leadingIcon={<Icons variant="line" label="arrow" />}
            onClick={onBack}
          />
          <Typography type={"h3"} classes="mg ml-4">
            {zoomMeetingId ? t("meeting.edit") : t("meeting.schedule")}
          </Typography>
        </Box>
        <Box>
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mt-10 mr-28"
          >
            {t("meeting.topic")}
          </Typography>
          <InputField
            placeholder={t("meeting.topicPlaceholder")}
            size="md"
            width={598}
            value={meetingInfo.topic}
            maxLength={200}
            disabled={
              !!zoomMeetingId &&
              !["ALL", "NONE"].includes(zoomMeetingId.split("/")[1])
            }
            onChange={(e) =>
              changeMeetingInfo({ ...meetingInfo, topic: e.target.value })
            }
          />
        </Box>
        <Box classes="mg mt-12">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mt-10 mr-28"
          >
            {t("common.desc")}
          </Typography>
          <InputArea
            placeholder={t("meeting.descPlaceholder")}
            width={598}
            height={80}
            maxLength={2000}
            value={meetingInfo.agenda}
            onChange={(e) =>
              changeMeetingInfo({ ...meetingInfo, agenda: e.target.value })
            }
          />
        </Box>
        <Box classes="mg mt-40">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mr-28"
          >
            {t("meeting.participants")}
          </Typography>
          <Box direction="column" width={598}>
            <Box>
              <UserSearchField
                width={467}
                position="absolute"
                dropdownWidth={467}
                type="search"
                isAllowNewUser={false}
                workgroupId={workgroupId}
                invisibleUserIds={participantList?.map((item) => item.userId)}
                onClick={(member) =>
                  changeParticipantList([...participantList, member])
                }
              />
              <Button
                btnStyle="fill"
                type="secondary"
                label={t("workgroup.selectUser")}
                classes="mg ml-8"
                whiteSpace="pre"
                size="lg"
                leadingIcon={<Icons variant="line" label="plusCircle" />}
                onClick={() => toggleWorkgroupMemberModal(true)}
              />
            </Box>
            <Box
              classes="meetingForm--user"
              height={isParticipantsListExtend ? 620 : 240}
            >
              {participantList?.map((item: Member) => (
                <Box>
                  <NameTag
                    user={item}
                    width={"calc(100% - 50px)"}
                    withoutUserId={false}
                    minHeight={52}
                    subLeftIcon={
                      workgroupState.workgroupDetail?.workgroup?.managerId ===
                      item?.userId ? (
                        <WorkgroupManagerIcon
                          style={{
                            position: "absolute",
                            bottom: "-5px",
                            insetInlineEnd: "-12px",
                          }}
                        />
                      ) : undefined
                    }
                  />
                  <Box
                    classes="mg ml-auto"
                    width={"fit-content"}
                    alignItems="center"
                  >
                    {loginDto.userInfo.userId === item.userId ? (
                      <Tag
                        backgroundColor={getColor(
                          "COLOR_BG_INTERACTIVE_BRAND_SECONDARY"
                        )}
                        style={{ height: "fit-content" }}
                        textColor={getColor("COLOR_TEXT_BRAND")}
                        text={t("meeting.host")}
                      />
                    ) : (
                      <Button
                        btnStyle="icon"
                        classes="mg ml-auto"
                        leadingIcon={<Icons variant="line" label="delete" />}
                        onClick={() =>
                          changeParticipantList(
                            participantList.filter(
                              (user) => user.userId !== item.userId
                            )
                          )
                        }
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box
              alignItems="center"
              width={"100%"}
              justifyContent="center"
              classes="cursor pointer"
              onClick={() =>
                changeParticipantsListExtend(!isParticipantsListExtend)
              }
            >
              <Icons
                variant="line"
                label="chevron"
                stroke={getColor("COLOR_ICON_INTERACTIVE_TERTIARY")}
                direction={isParticipantsListExtend ? "up" : "down"}
              />
            </Box>
          </Box>
        </Box>
        <Box classes="mg mt-40">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mt-10 mr-28"
          >
            {t("meeting.startTime")}
          </Typography>
          <Calendar
            width={"220px"}
            type={"single"}
            pickTime
            minDate={new Date()}
            selected={new Date(meetingInfo.start_time)}
            onSubmit={(date) =>
              changeMeetingInfo({
                ...meetingInfo,
                start_time: dayjs(date instanceof Date ? date : "").format(
                  "YYYY-MM-DDTHH:mm:ssZ"
                ),
              })
            }
          />
        </Box>
        <Box classes="mg mt-40">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mt-10 mr-28"
          >
            {t("meeting.length")}
          </Typography>
          <Dropdown
            dropdownMaxHeight={200}
            btnStyle="line"
            placeholder=" "
            width={224}
            value={t("meeting.minWithCount", { min: meetingInfo.duration })}
            buttonClick={buttonClick}
          >
            {Array(8)
              .fill(0)
              .map((item, index) => (
                <DropdownItem
                  label={`${t("meeting.minWithCount", {
                    min: (index + 1) * 5,
                  })}`}
                  onClick={() =>
                    handleChangeMeetingInfo("duration", (index + 1) * 5 + "")
                  }
                />
              ))}
          </Dropdown>
        </Box>
        <Box classes="mg mt-40">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mt-10 mr-28"
          >
            {t("meeting.timeline")}
          </Typography>
          <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Dropdown
              dropdownMaxHeight={200}
              btnStyle="line"
              placeholder=" "
              buttonClick={buttonClick}
              width={598}
              value={`(${utilsCommon.getFormatGMTOffset(
                zoomTimezone.filter(
                  (item) => item.id === meetingInfo.timezone
                )[0].offset
              )}) ${
                i18n.language.includes("ko")
                  ? zoomTimezone.filter(
                      (item) => item.id === meetingInfo.timezone
                    )[0].ko
                  : zoomTimezone.filter(
                      (item) => item.id === meetingInfo.timezone
                    )[0].en
              }`}
            >
              {zoomTimezone.map((item) => (
                <DropdownItem
                  label={`(${utilsCommon.getFormatGMTOffset(item.offset)}) ${
                    i18n.language.includes("ko") ? item.ko : item.en
                  }`}
                  checkColor={meetingInfo.timezone === item.id}
                  type="check"
                  checked={meetingInfo.timezone === item.id}
                  onClick={() => {
                    changeMeetingInfo({ ...meetingInfo, timezone: item.id });
                    setButtonClick(buttonClick + 1);
                  }}
                />
              ))}
            </Dropdown>
            <Tooltip
              message={t("common.noDemoVersion")}
              hideToolTip={!utilsCommon.isDemoEco()}
            >
              <Checkbox
                label={t("meeting.recurring.title")}
                classDiv="mg mt-8"
                disable={!!zoomMeetingId || utilsCommon.isDemoEco()}
                check={isReccurenceData}
                onClick={() => toggleRecurrenceData(!isReccurenceData)}
              />
            </Tooltip>
            {isReccurenceData && renderRepeatMeeting()}
          </Box>
        </Box>
        <Box classes="mg mt-40">
          <Typography
            type="subtitle2"
            width={220}
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            classes="mg mr-28"
          >
            {t("meeting.securtiy")}
          </Typography>
          <Box direction="column">
            <Box>
              <Checkbox label={t("meeting.password")} check disable />
              <Tooltip message={t("meeting.passowrdToolTip")}>
                <Icons variant="fill" label="questionmark" classes="mg ml-8" />
              </Tooltip>
            </Box>
            <InputField
              width={224}
              placeholder={t("meeting.passwordPlaceholder")}
              size="md"
              value={meetingInfo.password}
              maxLength={10}
              disabled={!!zoomMeetingId}
              onChange={(e) =>
                changeMeetingInfo({ ...meetingInfo, password: e.target.value })
              }
              classes="mg mt-12 ml-28 "
            />
            <Box classes="mg mt-28">
              <Checkbox
                label={t("meeting.waitingRoom")}
                check={meetingInfo.settings.waiting_room}
                onClick={() =>
                  changeMeetingInfo({
                    ...meetingInfo,
                    settings: {
                      ...meetingInfo.settings,
                      waiting_room: !meetingInfo.settings.waiting_room,
                    },
                  })
                }
              />
              <Tooltip message={t("meeting.waitingTooltip")}>
                <Icons variant="fill" label="questionmark" classes="mg ml-8" />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        minHeight={80}
        justifyContent="flex-end"
        alignItems="center"
        classes="meetingForm-footer"
      >
        <Button
          btnStyle="fill"
          type="secondary"
          label={t("common.cancel")}
          classes="mg mr-8"
          onClick={onBack}
        />
        <Button
          btnStyle="fill"
          label={zoomMeetingId ? t("common.edit") : t("common.save")}
          onClick={handleCreateOrUpdateMeeting}
          state={meetingInfo.topic.trim().length === 0 ? "disable" : "default"}
        />
      </Box>
    </Box>
  );
};

export default MeetingForm;
